import FontText from "jadro-pico-app/Components/UI/FontText/FontText";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css, keyframes, useTheme } from "styled-components";
import { FontSizeEnum, FontTypeEnum } from "../FontText/FontTextEnums";
import { useIsMobile } from "../Layouts/AppLayout/IsMobileHook";
import { ThemeColorEnum } from "../Themes/ThemeEnums";
import { ITheme } from "../Themes/ThemeTypes";
import { appSetSnackbar, NO_SNACKBAR_ID } from "jadro-pico-app/Redux/AppCoreReducer";
import { AppCoreState } from "jadro-pico-app/Redux/AppCoreReducer";
import { TextButton } from "../Button";
import { consumeSnackbarAction, clearSnackbarAction, SNACKBAR_TIMEOUT_MS } from "./showSnackbar";
import { useFab } from "../../../Context/FabContext";
import {
  FAB_HEIGHT_PX,
  FAB_VIEWPORT_OFFSET_PX,
} from "../Button/ExtendedFabButton/ExtendedFabButton";

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(20px);
    opacity: 0;
  }
`;

const SNACKBAR_CONTENT_OFFSET_PX = 32;
const FAB_CLEARANCE_PX = FAB_VIEWPORT_OFFSET_PX + FAB_HEIGHT_PX + FAB_VIEWPORT_OFFSET_PX;

const Div = styled.div<{
  theme: ITheme;
  $isMobile: boolean;
  $isVisible: boolean;
  $bottomPx: number;
}>`
  background-color: rgb(${(props) => props.theme.colors.inverse_surface});
  border-radius: 4px;
  position: absolute;
  z-index: ${(props) => props.theme.base.z_index.snackbar};
  bottom: ${(props) => props.$bottomPx}px;
  padding-left: 16px;
  padding-right: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  min-height: 48px;
  box-shadow: ${(props) => props.theme.base.elevation.level3_shadow};
  animation: ${(props) =>
    props.$isVisible
      ? css`
          ${slideIn} ${props.theme.base.motion.duration.medium2}ms ease-out forwards
        `
      : css`
          ${slideOut} ${props.theme.base.motion.duration.medium2}ms ease-in forwards
        `};

  left: 16px;
  right: 16px;
  ${(props) =>
    !props.$isMobile &&
    css`
      max-width: 400px;
    `};
`;

const Snackbar: React.FC = () => {
  const theme = useTheme() as ITheme;
  const exitDurationMs = theme.base.motion.duration.medium2;
  const isMobile = useIsMobile();
  const { fab } = useFab();
  const dispatch = useDispatch();

  const supportingText = useSelector(
    (state: { core: AppCoreState }) => state.core.snackbarSupportingText,
  );
  const actionLabel = useSelector(
    (state: { core: AppCoreState }) => state.core.snackbarActionLabel,
  );
  const snackbarId = useSelector((state: { core: AppCoreState }) => state.core.snackbarId);

  const [dismissedId, setDismissedId] = useState<number>(NO_SNACKBAR_ID);
  const isVisible = supportingText !== null && snackbarId !== dismissedId;

  useEffect(() => {
    if (!isVisible) return undefined;
    const timer = setTimeout(() => setDismissedId(snackbarId), SNACKBAR_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isVisible, snackbarId]);

  useEffect(() => {
    if (isVisible || !supportingText) return undefined;
    const timer = setTimeout(() => {
      dispatch(appSetSnackbar({ text: null, actionLabel: null, id: NO_SNACKBAR_ID }));
      clearSnackbarAction();
    }, exitDurationMs);
    return () => clearTimeout(timer);
  }, [isVisible, supportingText, dispatch, exitDurationMs]);

  const handleAction = (): void => {
    consumeSnackbarAction();
    dispatch(appSetSnackbar({ text: null, actionLabel: null, id: NO_SNACKBAR_ID }));
  };

  return (
    supportingText && (
      <Div
        $isMobile={isMobile}
        $isVisible={isVisible}
        $bottomPx={isMobile && fab ? FAB_CLEARANCE_PX : SNACKBAR_CONTENT_OFFSET_PX}
      >
        <FontText
          text={supportingText}
          $fontSize={FontSizeEnum.MEDIUM}
          $fontType={FontTypeEnum.BODY}
          $color={ThemeColorEnum.INVERSE_ON_SURFACE}
        />
        {actionLabel && <TextButton label={actionLabel} onClick={handleAction} />}
      </Div>
    )
  );
};

export default Snackbar;
